// src/components/Login.js
import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  TextField, Button, Box, Typography, Link, CircularProgress
} from '@mui/material';
import { styled } from '@mui/system';
import { jwtDecode } from 'jwt-decode';
import { AuthContext } from '../../../context/AuthContext';
import ForgotPasswordDialog from '../../ForgotPassword';
import { useLocation } from 'react-router-dom';

const Root = styled(Box)({
  height: '100vh',
  display: 'flex',
  backgroundColor: 'black',
});

const LeftSide = styled(Box)({
  flex: 7,
  backgroundColor: '#282c34',
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '40px',
  textAlign: 'center',
});

const RightSide = styled(Box)({
  flex: 3,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'white',
  padding: '20px',
});

const FormContainer = styled(Box)({
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
});

const ErrorText = styled(Typography)({
  color: 'red',
  marginBottom: '10px',
});

const SignUpLabel = styled(Typography)({
  marginTop: '10px',
  color: '#1976d2',
  cursor: 'pointer',
});

const Login = () => {
  const location = useLocation();
  const from = location.state?.from || { pathname: '/2.0/udyogconnect-home' };

  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { login, setIsLoggingOut } = useContext(AuthContext);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');

  const [knowabout, setknowabout] = useState('');
  const [resonforjoining, setresonforjoining] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/login`, {
        email,
        password,
      });
      const token = response.data.token;
      login(token);
      setIsLoggingOut(false);
      localStorage.setItem('token', token);
      const decoded = jwtDecode(token);
      localStorage.setItem('accessLevel', decoded.accessLevel);
      localStorage.setItem('isprovider', decoded.isprovider === 1 ? 'yes' : 'no');
      localStorage.setItem('userType', decoded.userType || 'user');
      localStorage.setItem('isEnterprise', decoded.isEnterprise ? 'yes' : 'no');
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/signup`, {
        email,
        firstName,
        lastName,
        mobile,
        knowabout,
        resonforjoining
      });

      if (response.data.success) {
        alert('Signup complete pending for approval!');
        setIsSignUp(false);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Error during signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleForm = () => {
    setIsSignUp(!isSignUp);
    setError('');
  };

  return (
    <Box
  sx={{
    width: "100%",
    maxWidth: 380,
    margin: "auto",
    padding: "10px",
  }}
>
  <Box
    sx={{
      background: "#fff",
      borderRadius: "16px",
      padding: "25px",
      boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
      transition: "all 0.3s ease",
    }}
  >
    {/* HEADER */}
    <Typography
      variant="h6"
      sx={{
        fontWeight: 600,
        textAlign: "center",
        mb: 1,
      }}
    >
      {isSignUp ? "Create Account" : "Welcome Back"}
    </Typography>

    <Typography
      variant="body2"
      sx={{ textAlign: "center", color: "#777", mb: 2 }}
    >
      {isSignUp
        ? "Join Udyog Connect"
        : "Login to continue"}
    </Typography>

    {/* FORM */}
    <form onSubmit={isSignUp ? handleSignUp : handleLogin}>
      {isSignUp && (
        <>
          <TextField label="First Name" fullWidth size="small" sx={{ mb: 1.5 }} onChange={(e) => setFirstName(e.target.value)} />
          <TextField label="Last Name" fullWidth size="small" sx={{ mb: 1.5 }} onChange={(e) => setLastName(e.target.value)} />
          <TextField label="Mobile" fullWidth size="small" sx={{ mb: 1.5 }} onChange={(e) => setMobile(e.target.value)} />
          <TextField label="How did you know about us?" multiline rows={2} fullWidth size="small" sx={{ mb: 1.5 }} onChange={(e) => setknowabout(e.target.value)} />
          <TextField label="Reason for joining" multiline rows={2} fullWidth size="small" sx={{ mb: 1.5 }} onChange={(e) => setresonforjoining(e.target.value)} />
        </>
      )}

      <TextField
        label="Email"
        fullWidth
        size="small"
        required
        sx={{ mb: 1.5 }}
        onChange={(e) => setEmail(e.target.value)}
      />

      {!isSignUp && (
        <TextField
          label="Password"
          type="password"
          fullWidth
          size="small"
          required
          sx={{ mb: 1.5 }}
          onChange={(e) => setPassword(e.target.value)}
        />
      )}

      {error && (
        <Typography sx={{ color: "red", mb: 1, fontSize: "13px" }}>
          {error}
        </Typography>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{
          height: 40,
          borderRadius: "8px",
          background: "linear-gradient(135deg, #385170, #1f3b57)",
          fontWeight: 600,
          fontSize: "14px",
          "&:hover": {
            background: "linear-gradient(135deg, #2c4058, #162938)",
          },
        }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={20} /> : isSignUp ? "Sign Up" : "Login"}
      </Button>
    </form>

    {/* FOOTER */}
    <Box sx={{ textAlign: "center", mt: 2 }}>
      {isSignUp ? (
        <>
          <Typography variant="body2">
            Already have an account?{" "}
            <Link onClick={toggleForm} sx={{ cursor: "pointer" }}>
              Login
            </Link>
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body2">
            Don’t have an account?{" "}
            <Link onClick={toggleForm} sx={{ cursor: "pointer" }}>
              Sign Up
            </Link>
          </Typography>

          <Link
            component="button"
            onClick={() => setShowForgotPassword(true)}
            sx={{ fontSize: "13px", mt: 1 }}
          >
            Forgot Password?
          </Link>
        </>
      )}
    </Box>
  </Box>

  <ForgotPasswordDialog
    open={showForgotPassword}
    onClose={() => setShowForgotPassword(false)}
  />
</Box>
  );
};

export default Login;
